// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Feature Comparison Page Route
 *
 * Public page showing all pricing features in a comparison table.
 * Accessible without authentication, linked from pricing cards.
 *
 * @module routes/features
 */

import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
	KeyRound,
	Loader2,
	MonitorSmartphone,
	ShieldCheck,
	WandSparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { config, features as appFeatures } from "@/lib/config";
import { Header, Footer } from "@/components/landing";
import { areAllCapacityAddOnsAvailable } from "@/components/landing-v2/landing-faq";
import { FeatureComparisonTable } from "@/components/pricing";
import {
	useFeatureCatalog,
	usePricingConfig,
} from "@/hooks/usePricingConfig";
import { usePublicFeatureFlags } from "@/hooks/usePublicFeatureFlags";
import { arePaymentsVisibleOnPlatform } from "@/native/ios-payments-gate";

/**
 * Maps comparison feature keys to platform feature flag keys.
 * Features not in this map are always shown.
 */
const COMPARISON_FEATURE_FLAG_MAP: Record<string, string> = {
	maxAiMessagesPerMonth: "ai_assistant",
	maxWebsiteClonesPerMonth: "websmith",
	maxAppStudioProjects: "app_studio",
	teamMembers: "team_collaboration",
	customDomains: "custom_domains",
};

export const Route = createFileRoute("/features")({
	component: FeaturesPage,
});

function FeaturesPage() {
	if (!arePaymentsVisibleOnPlatform()) {
		return <ZeroKnowledgeFeaturesPage />;
	}

	return <FeatureComparisonPage />;
}

function FeatureComparisonPage() {
	const { t, i18n } = useTranslation(["pricing-features", "common"]);
	const siteUrl = config.siteUrl;
	const skipToContent = t("common:a11y.skipToContent");

	const { data: catalog, isLoading, error } = useFeatureCatalog();
	const { data: pricingConfig } = usePricingConfig();
	const { data: flags } = usePublicFeatureFlags();
	const addOns =
		pricingConfig?.addOns?.filter((item) => item.available) ?? [];

	// Filter comparison features based on active flags
	const filteredFeatures = useMemo(() => {
		if (!catalog?.features) return [];
		if (!flags) return catalog.features;
		return catalog.features.filter((feat) => {
			const flagKey = COMPARISON_FEATURE_FLAG_MAP[feat.key];
			if (!flagKey) return true; // No mapping = always shown
			return flags[flagKey] ?? true;
		});
	}, [catalog?.features, flags]);

	// When payments are disabled, the app is fully free: drop paid/custom tier
	// columns so the comparison shows only the free feature set (no prices,
	// no upgrade CTAs).
	const visibleTiers = useMemo(() => {
		if (!catalog?.tiers) return [];
		if (appFeatures.payments) return catalog.tiers;
		return catalog.tiers.filter(
			(tier) => !tier.isCustomPricing && tier.monthlyPriceCents === 0,
		);
	}, [catalog?.tiers]);

	return (
		<>
			<Helmet>
				<title>{t("comparison.title")} | {t("common:app.name")}</title>
				<meta name="description" content={t("comparison.subtitle")} />
				<meta name="robots" content="index, follow" />
				<link rel="canonical" href={`${siteUrl}/features`} />

				<meta property="og:type" content="website" />
				<meta property="og:url" content={`${siteUrl}/features`} />
				<meta
					property="og:title"
					content={`${t("comparison.title")} | ${t("common:app.name")}`}
				/>
				<meta property="og:description" content={t("comparison.subtitle")} />
				<meta property="og:site_name" content={t("common:app.name")} />

				<meta name="twitter:card" content="summary" />
				<meta name="twitter:site" content="@pssmngr" />
				<meta
					name="twitter:title"
					content={`${t("comparison.title")} | ${t("common:app.name")}`}
				/>
				<meta name="twitter:description" content={t("comparison.subtitle")} />
			</Helmet>

			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground"
			>
				{skipToContent === "common:a11y.skipToContent"
					? "Skip to main content"
					: skipToContent}
			</a>

			<div className="min-h-screen bg-background text-foreground">
				<Header />

				<main id="main-content" role="main" className="landing-page-wash">
					<div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-28 sm:pb-24 sm:pt-32">
						{isLoading ? (
							<div className="flex items-center justify-center py-24">
								<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
								<span className="ms-2 text-muted-foreground">
									{t("comparison.loading")}
								</span>
							</div>
						) : error ? (
							<div className="text-center text-destructive py-24">
								<p>{t("comparison.loadError")}</p>
							</div>
						) : catalog ? (
							<div className="landing-panel overflow-x-auto p-4 sm:p-6">
								<FeatureComparisonTable
									features={filteredFeatures}
									tiers={visibleTiers}
								/>
							</div>
						) : null}
						{addOns.length > 0 && (
							<aside className="landing-card mx-auto mt-10 max-w-3xl p-6">
								<h2 className="text-lg font-semibold">
									{t("comparison.addOns.title")}
								</h2>
								<p className="mt-1 text-sm text-muted-foreground">
									{t("comparison.addOns.description")}
								</p>
								<ul className="mt-4 grid gap-3 sm:grid-cols-2">
									{addOns.map((item) => (
										<li
											key={item.kind}
											className="rounded-xl border border-border p-4"
										>
											<p className="font-medium">
												{t(
													`comparison.addOns.items.${item.kind}`,
												)}
											</p>
											<p className="mt-1 text-sm text-muted-foreground">
												{t(
													`comparison.addOns.price.${item.kind}`,
													{
													price: new Intl.NumberFormat(
														i18n.resolvedLanguage ??
															i18n.language,
														{
															style: "currency",
															currency:
																item.currency.toUpperCase(),
														},
													).format(
														item.priceInCents / 100,
													),
													},
												)}
											</p>
										</li>
									))}
								</ul>
								{areAllCapacityAddOnsAvailable(
									pricingConfig?.addOns,
								) && (
									<p className="mt-4 text-sm text-muted-foreground">
										{t("comparison.addOns.note")}
									</p>
								)}
							</aside>
						)}
					</div>
				</main>

				<Footer />
			</div>
			</>
		);
	}

const MARKETING_FEATURE_KEYS = [
	"vault",
	"autofill",
	"generator",
	"everywhere",
] as const;

const MARKETING_FEATURE_ICONS = {
	vault: ShieldCheck,
	autofill: KeyRound,
	generator: WandSparkles,
	everywhere: MonitorSmartphone,
} as const;

function ZeroKnowledgeFeaturesPage() {
	const { t } = useTranslation(["landing", "common"]);
	const siteUrl = config.siteUrl;
	const skipToContent = t("common:a11y.skipToContent");

	return (
		<>
			<Helmet>
				<title>{t("features.headline")} | {t("common:app.name")}</title>
				<meta name="description" content={t("features.subtitle")} />
				<meta name="robots" content="index, follow" />
				<link rel="canonical" href={`${siteUrl}/features`} />

				<meta property="og:type" content="website" />
				<meta property="og:url" content={`${siteUrl}/features`} />
				<meta
					property="og:title"
					content={`${t("features.headline")} | ${t("common:app.name")}`}
				/>
				<meta property="og:description" content={t("features.subtitle")} />
				<meta property="og:site_name" content={t("common:app.name")} />
			</Helmet>

			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground"
			>
				{skipToContent === "common:a11y.skipToContent"
					? "Skip to main content"
					: skipToContent}
			</a>

			<div className="min-h-screen bg-background text-foreground">
				<Header />

				<main id="main-content" role="main">
					<section className="landing-page-wash container mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-28 sm:pb-24 sm:pt-32">
						<div className="mx-auto max-w-3xl text-center">
							<div className="landing-eyebrow mb-4">
								{t("hero.badges.zeroKnowledge")}
							</div>
							<h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
								{t("features.headline")}
							</h1>
							<p className="mt-4 text-lg text-muted-foreground">
								{t("features.subtitle")}
							</p>
							<div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
								<Button asChild size="lg" className="h-12 rounded-full px-7">
									<Link to="/register">{t("hero.cta.primary")}</Link>
								</Button>
								<Button asChild variant="outline" size="lg" className="h-12 rounded-full bg-card px-7">
									<Link to="/" hash="faq">
										{t("faq.headline")}
									</Link>
								</Button>
							</div>
						</div>

						<div className="mt-14 grid gap-4 sm:grid-cols-2">
							{MARKETING_FEATURE_KEYS.map((key) => {
								const Icon = MARKETING_FEATURE_ICONS[key];

								return (
									<div
										key={key}
										className="landing-card p-6 text-card-foreground"
									>
										<div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--landing-glow)/0.1)] landing-text-accent">
											<Icon className="h-5 w-5" aria-hidden="true" />
										</div>
										<h2 className="text-xl font-semibold">
											{t(`features.items.${key}.title`)}
										</h2>
										<p className="mt-2 text-muted-foreground">
											{t(`features.items.${key}.description`)}
										</p>
									</div>
								);
							})}
						</div>
					</section>
				</main>

				<Footer />
			</div>
		</>
	);
}
